import { computed, inject, Injectable } from '@angular/core'

import { environment } from '../../environments/environment'

import { UserStore } from '../store/user/user.store'

import { RoleStore } from '../store/roles/roles.store'

import { DataScope, Permission } from './permission.types'

import { LEGACY_ROLE, LegacyRoleCode } from './legacy/legacy-role.ids'

import {
  hasSupportedLegacyRole,
  extractRoleIdsFromUser,
  isTeamScopeLegacyRole,
  resolveLegacyRoleCodes,
} from './legacy/legacy-role.resolver'

import { canLegacyUser } from './legacy/legacy-access.util'

import { permissionsForLegacyRoles } from './legacy/legacy-role-permissions.config'

import { permissionsFromRolesJson } from './modern/permissions-json.resolver'

import { DEFAULT_ROUTE_CANDIDATES } from './permission-routes.config'

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly userStore = inject(UserStore)

  private readonly roleStore = inject(RoleStore)

  readonly isLegacyMode = environment.profileApiModel === 'legacy'

  readonly legacyRoleCodes = computed<LegacyRoleCode[]>(() => {
    this.userStore.user()

    return resolveLegacyRoleCodes(this.userStore.user())
  })

  readonly hasSupportedRole = computed(() => {
    this.userStore.user()

    this.roleStore.get()

    if (hasSupportedLegacyRole(this.userStore.user())) return true

    if (!this.isLegacyMode && this.modernPermissions().size > 0) return true

    return false
  })

  private readonly legacyPermissions = computed<Set<Permission>>(() => {
    return permissionsForLegacyRoles(this.legacyRoleCodes())
  })

  private readonly modernPermissions = computed<Set<Permission>>(() => {
    if (this.isLegacyMode) return new Set()

    return permissionsFromRolesJson(this.roleStore.get().entities)
  })

  /** Reactive snapshot used by directives and templates. */

  readonly activePermissions = computed<Set<Permission>>(() => {
    this.userStore.user()

    this.roleStore.get()

    if (!this.isLegacyMode) {
      const modern = this.modernPermissions()

      if (modern.size > 0) return modern

      if (hasSupportedLegacyRole(this.userStore.user())) return this.legacyPermissions()

      return new Set()
    }

    if (!hasSupportedLegacyRole(this.userStore.user())) return new Set()

    return this.legacyPermissions()
  })

  readonly dataScope = computed<DataScope>(() => {
    const codes = this.legacyRoleCodes()

    if (codes.length) {
      if (codes.includes(LEGACY_ROLE.DOCTOR_ADMIN)) return 'global'

      if (codes.some((code) => isTeamScopeLegacyRole(code))) return 'team'

      return 'assigned_services'
    }

    if (!this.isLegacyMode) {
      const roles = this.roleStore.get().entities

      for (const role of roles) {
        const roleId = Number(role.id)

        if (roleId === LEGACY_ROLE.DOCTOR_ADMIN) return 'global'

        if (roleId === LEGACY_ROLE.DOCTOR_DIRECTOR || roleId === LEGACY_ROLE.OPERATOR_ADMIN) return 'team'
      }
    }

    return 'assigned_services'
  })

  can(permission: Permission): boolean {
    const user = this.userStore.user()

    if (!this.isLegacyMode) {
      const modern = this.modernPermissions()

      if (modern.size > 0) return modern.has(permission)

      return canLegacyUser(user, permission)
    }

    return canLegacyUser(user, permission)
  }

  canAny(...permissions: Permission[]): boolean {
    if (!permissions.length) return false

    return permissions.some((p) => this.can(p))
  }

  canAll(...permissions: Permission[]): boolean {
    if (!permissions.length) return false

    return permissions.every((p) => this.can(p))
  }

  canImportPatients(): boolean {
    return this.can(Permission.PatientsImport)
  }

  /** View consultation report (elaborate with ConsultationReport, or read-only with history + HC). */

  canViewConsultationReport(): boolean {
    return (
      this.can(Permission.ConsultationReport) ||
      (this.can(Permission.ConsultationsHistory) && this.can(Permission.PatientsClinicalHistory))
    )
  }

  legacyRoleIds(): number[] {
    return extractRoleIdsFromUser(this.userStore.user())
  }

  getDefaultRoute(): string {
    for (const { path, permission } of DEFAULT_ROUTE_CANDIDATES) {
      if (this.can(permission)) return path
    }

    return '/sin-permisos'
  }
}
